import React, { useState, useRef, useEffect } from 'react';
import $ from 'jquery';
import LazyImage from 'views/ui/lazy_image';
import cn from 'classnames';

const Slideshow = ({
  isVisible = false,
  parentViewport = null,
  photos = [],
  mediaSetting = {},
  onClose,
  currentVideoTime,
  handleVideoProgress,
}) => {
  const [visiblePhotoIndex, setVisiblePhotoIndex] = useState(0);
  const [isLazyPlayerVisible, setLazyPlayerVisible] = useState(false);
  const [isBrowserFocused, setBrowserFocused] = useState(true);
  let photoLength = photos.length;
  const isMediaPresent =
    mediaSetting &&
    mediaSetting.hasOwnProperty('media_link') &&
    mediaSetting['media_link'] !== null;

  if (isMediaPresent) {
    photoLength = photoLength + 1;
  }
  const dimmerRef = useRef(null);
  const videoPlayerRef = useRef(null);

  const setVisibility = () => {
    const dimmerAction = isVisible ? 'show' : 'hide';
    if (dimmerRef.current) {
      $(dimmerRef.current).dimmer(dimmerAction);
    }
  };

  useEffect(() => {
    setVisibility();
  });

  useEffect(() => {
    // Start video from timestamp of main video player on open
    videoPlayerRef?.current?.seekTo(currentVideoTime);
  }, [currentVideoTime]);

  let contentStyle = {};

  if (parentViewport && parentViewport.isConsistent) {
    contentStyle = {
      position: 'relative',
      top: Math.max(
        parentViewport.centersDelta +
          window.innerHeight / 2 -
          parentViewport.height / 2,
        0
      ),
      height: parentViewport.height,
    };
  }

  const galleryScroll = (direction) => {
    if (photoLength < 2) return;
    let currentIndex = visiblePhotoIndex;

    currentIndex += (direction === 'next' ? +1 : -1) + photoLength;
    currentIndex %= photoLength;

    setVisiblePhotoIndex(currentIndex);
  };

  const keyupHandler = (e) => {
    if (!isVisible) {
      return;
    }

    switch (e.key) {
      case 'Escape':
        onClose();
        setLazyPlayerVisible(false);
        break;

      // left, h, j - vim mode
      case 'ArrowLeft':
      case 'h':
      case 'j':
        galleryScroll('prev');
        break;

      // right, k, l - vim mode
      case 'ArrowRight':
      case 'k':
      case 'l':
        galleryScroll('next');
        break;
      default:
        break;
    }
  };

  const renderPhoto = (photo, idx) => {
    const isActive = idx === visiblePhotoIndex;
    const shouldPreload = idx === visiblePhotoIndex + 1;

    return (
      <LazyImage
        activate={isActive || shouldPreload}
        className={cn('gallery-item', { active: isActive })}
        key={idx}
        position={idx + 1}
        src={photo}
        totalImages={photoLength}
        isLazyPlayerVisible={isLazyPlayerVisible}
        isBrowserFocused={isBrowserFocused}
        startTime={currentVideoTime}
        handleVideoProgress={handleVideoProgress}
        playerRef={videoPlayerRef}
      />
    );
  };

  let consolidatedMedia = [...photos, { ...mediaSetting, media: true }];

  if (mediaSetting.display_at_start) {
    consolidatedMedia = [{ ...mediaSetting, media: true }, ...photos];
  }

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const lazyImg = event.target.closest('.gallery-item');
      const btnNext = event.target.closest('.btn-next');
      const btnPrev = event.target.closest('.btn-prev');
      if (!lazyImg && !btnNext && !btnPrev && isVisible) {
        onClose();
      }
    };

    const handleVisibilityChange = () => {
      setBrowserFocused(!document.hidden);
    };

    // Check if the current photo is an object and update isLazyPlayerVisible accordingly
    if (typeof consolidatedMedia[visiblePhotoIndex] === 'object' && isVisible) {
      setLazyPlayerVisible(true);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    } else {
      setLazyPlayerVisible(false);
    }

    window.addEventListener('mousedown', handleOutsideClick);

    return () => {
      // Clean up the event listeners when the component unmounts
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [visiblePhotoIndex, consolidatedMedia, isVisible]);

  return (
    <div
      aria-label="image carousel"
      aria-roledescription="carousel"
      className="Slideshow ui page dimmer"
      ref={dimmerRef}
      role="region"
    >
      <div className="content" style={contentStyle}>
        <button
          aria-label="Close Slideshow"
          className="btn-close"
          onClick={() => {
            onClose();
            setLazyPlayerVisible(false);
          }}
          onKeyUp={keyupHandler}
          type="button"
        >
          <i className="close-icon af-remove" />
        </button>
        {photoLength > 1 && (
          <>
            <button
              aria-label="previous image in Slideshow"
              className="btn-prev"
              onClick={() => galleryScroll('prev')}
              onKeyUp={(e) => keyupHandler(e)}
              type="button"
            >
              <i className="icon af-angle-left-light" />
            </button>
            <button
              aria-label="next image in Slideshow"
              className="btn-next"
              onClick={() => galleryScroll('next')}
              onKeyUp={(e) => keyupHandler(e)}
              type="button"
            >
              <i className="icon af-angle-right-light" />
            </button>
          </>
        )}
        {consolidatedMedia.map((photo, idx) => renderPhoto(photo, idx))}
      </div>
    </div>
  );
};

Slideshow.displayName = 'Slideshow';

export default Slideshow;
